"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Locale, detectLocale, createTranslator, locales } from './index';

type TranslateFunction = (key: string, params?: Record<string, string | number>) => string;

interface I18nContextType {
    locale: Locale;
    setLocale: (locale: Locale) => void;
    t: TranslateFunction;
}

const I18nContext = createContext<I18nContextType | null>(null);

const LOCALE_STORAGE_KEY = 'myfacture-locale';

interface I18nProviderProps {
    children: React.ReactNode;
    /** If set, forces this locale (e.g., from user DB preference) */
    initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
    const [locale, setLocaleState] = useState<Locale>(() => {
        // Priority: initialLocale (from DB) > localStorage > browser detection > 'fr'
        if (initialLocale && locales.includes(initialLocale)) return initialLocale;

        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem(LOCALE_STORAGE_KEY) as Locale;
            if (stored && locales.includes(stored)) return stored;
        }

        return detectLocale();
    });

    const setLocale = useCallback((newLocale: Locale) => {
        if (!locales.includes(newLocale)) return;
        setLocaleState(newLocale);
        if (typeof window !== 'undefined') {
            localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
        }
        // Update the <html lang> attribute
        document.documentElement.lang = newLocale;
    }, []);

    // Sync locale when initialLocale changes (e.g., user logs in, DB pref loaded)
    useEffect(() => {
        if (initialLocale && locales.includes(initialLocale)) {
            setLocaleState(initialLocale);
            localStorage.setItem(LOCALE_STORAGE_KEY, initialLocale);
            document.documentElement.lang = initialLocale;
        }
    }, [initialLocale]);

    // Set <html lang> on mount
    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const t = useMemo(() => createTranslator(locale), [locale]);

    const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    );
}

/**
 * Hook to access i18n context.
 * Returns { locale, setLocale, t }
 *
 * Usage:
 *   const { t } = useTranslation();
 *   <span>{t('nav.dashboard')}</span>
 */
export function useTranslation() {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error('useTranslation must be used within an I18nProvider');
    }
    return context;
}
